'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import { HubPill } from '@/features/hub/shared/HubComponents';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { canCurrentUserManageHubTeam } from '../teamRoles';

const ROLE_OPTIONS = [
  'manager',
  'captain',
  'organizer',
  'coach',
  'player',
  'reserve',
];

export default function HubTeamRosterPanel({
  team,
  currentUser,
  messages = {},
}) {
  const router = useRouter();
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);
  const canManage = canCurrentUserManageHubTeam(team, currentUser);
  const [members, setMembers] = useState(
    Array.isArray(team?.members) ? team.members : [],
  );
  const [draftRoles, setDraftRoles] = useState(() =>
    Object.fromEntries(
      (Array.isArray(team?.members) ? team.members : []).map((member) => [
        member.userNumber,
        member.role || 'player',
      ]),
    ),
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeActionKey, setActiveActionKey] = useState('');

  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS.map((role) => ({
        value: role,
        label: msg(`teams.roles.${role}`, role),
      })),
    [messages],
  );

  const updateMemberRole = async (userNumber) => {
    const nextRole = draftRoles[userNumber] || 'player';
    const activeKey = `${userNumber}:role`;
    const targetMember = members.find(
      (member) => member.userNumber === userNumber,
    );

    setErrorMessage('');
    setStatusMessage('');
    setActiveActionKey(activeKey);

    try {
      const response = await clientFetch(
        `/api/hub/teams/${team.teamNumber}/members/${encodeURIComponent(userNumber)}`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ role: nextRole }),
        },
      );

      setMembers((currentMembers) =>
        currentMembers.map((member) =>
          member.userNumber === userNumber
            ? {
                ...member,
                role: response?.member?.role || nextRole,
              }
            : member,
        ),
      );
      setDraftRoles((currentDraftRoles) => ({
        ...currentDraftRoles,
        [userNumber]: response?.member?.role || nextRole,
      }));
      setStatusMessage(
        msg('teams.roster.roleUpdated', 'Updated the role for {{name}}.', {
          name: targetMember?.name || targetMember?.userNumber || userNumber,
        }),
      );
      router.refresh();
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.roster.errors.updateRole',
            'Unable to update the member role.',
          ),
      );
    } finally {
      setActiveActionKey('');
    }
  };

  const removeMember = async (userNumber) => {
    const activeKey = `${userNumber}:remove`;
    const targetMember = members.find(
      (member) => member.userNumber === userNumber,
    );

    setErrorMessage('');
    setStatusMessage('');
    setActiveActionKey(activeKey);

    try {
      await clientFetch(
        `/api/hub/teams/${team.teamNumber}/members/${encodeURIComponent(userNumber)}`,
        {
          method: 'DELETE',
        },
      );

      setMembers((currentMembers) =>
        currentMembers.filter((member) => member.userNumber !== userNumber),
      );
      setDraftRoles((currentDraftRoles) => {
        const nextDraftRoles = { ...currentDraftRoles };
        delete nextDraftRoles[userNumber];
        return nextDraftRoles;
      });
      setStatusMessage(
        msg('teams.roster.memberRemoved', 'Removed {{name}} from the team.', {
          name: targetMember?.name || targetMember?.userNumber || userNumber,
        }),
      );
      router.refresh();
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.roster.errors.removeMember',
            'Unable to remove this member from the team.',
          ),
      );
    } finally {
      setActiveActionKey('');
    }
  };

  return (
    <section className="hub-team-card hub-team-card--detail-panel">
      <div className="hub-team-panel__header">
        <p className="hub-team-card__eyebrow">
          {msg('teams.detail.membersTitle', 'Roster')}
        </p>
        <p className="hub-team-card__copy">
          {canManage
            ? msg(
                'teams.roster.managementHint',
                'Review the current roster, assign roles, and remove members directly from the team page.',
              )
            : msg(
                'teams.detail.membersSubtitle',
                'Current members and owners attached to this team identity.',
              )}
        </p>
      </div>

      {statusMessage ? (
        <div className="auth-message auth-message--success">
          {statusMessage}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      {members.length ? (
        <div className="hub-team-member-list">
          {members.map((member) => {
            const currentRole =
              draftRoles[member.userNumber] || member.role || 'player';
            const canManageMember = canManage && !member.isOwner;
            const roleIsDirty = currentRole !== (member.role || 'player');

            return (
              <article key={member.userNumber} className="hub-team-member-card">
                <div className="hub-team-member-card__header">
                  <div className="d-flex flex-column gap-2">
                    <strong className="hub-team-member-card__name">
                      {member.name ||
                        msg('teams.detail.memberFallback', 'Unnamed member')}
                    </strong>
                    <span className="hub-team-member-card__meta">
                      {member.userNumber}
                    </span>
                  </div>

                  <HubPill tone={member.isOwner ? 'accent' : 'games'}>
                    {msg(`teams.roles.${member.role}`, member.role || 'player')}
                  </HubPill>
                </div>

                {canManageMember ? (
                  <div className="hub-team-member-card__controls">
                    <FormField
                      as="select"
                      label={msg('teams.roster.roleLabel', 'Role')}
                      name={`role-${member.userNumber}`}
                      value={currentRole}
                      onChange={(event) =>
                        setDraftRoles((currentDraftRoles) => ({
                          ...currentDraftRoles,
                          [member.userNumber]: event.target.value,
                        }))
                      }
                      options={roleOptions}
                    />

                    <div className="hub-team-member-card__actions">
                      <button
                        type="button"
                        className="app-button app-button--primary app-button--compact"
                        onClick={() => updateMemberRole(member.userNumber)}
                        disabled={Boolean(activeActionKey) || !roleIsDirty}
                      >
                        {activeActionKey === `${member.userNumber}:role`
                          ? msg('teams.roster.updatingRole', 'Saving role...')
                          : msg('teams.roster.updateRole', 'Save role')}
                      </button>

                      <button
                        type="button"
                        className="app-button app-button--ghost app-button--compact"
                        onClick={() => removeMember(member.userNumber)}
                        disabled={Boolean(activeActionKey)}
                      >
                        {activeActionKey === `${member.userNumber}:remove`
                          ? msg('teams.roster.removingMember', 'Removing...')
                          : msg('teams.roster.removeMember', 'Remove member')}
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <p className="hub-profile-card__microcopy">
          {msg(
            'teams.detail.membersEmpty',
            'No members are listed for this team yet.',
          )}
        </p>
      )}
    </section>
  );
}
